import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import type { AppRole } from "@/lib/constants";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: AppRole;
  redirectTo?: string;
}

export function ProtectedRoute({
  children,
  requiredRole,
  redirectTo = "/login"
}: ProtectedRouteProps) {
  const { user, loading, hasRole } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background dark" role="status" aria-label="Loading">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (!user.email_confirmed_at) {
    const emailQuery = user.email ? `?email=${encodeURIComponent(user.email)}` : "";
    return (
      <Navigate
        to={`/verify-email${emailQuery}`}
        state={{ from: location, unverified: true, email: user.email }}
        replace
      />
    );
  }

  if (requiredRole && !hasRole(requiredRole) && !hasRole("super_admin")) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
